import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  limitOptions?: number[];
  darkMode?: boolean;
}

function Pagination({
  currentPage,
  totalPages,
  totalItems,
  limit,
  onPageChange,
  onLimitChange,
  limitOptions = [2, 5, 10, 20, 50],
  darkMode = false,
}: PaginationProps) {
  // Theme classes
  const t = darkMode
    ? {
        text: "text-gray-300",
        textMuted: "text-gray-500",
        textDisabled: "text-gray-600",
        btnBg: "bg-gray-800 border-gray-600",
        btnHover: "hover:bg-gray-700",
        btnText: "text-gray-300",
        pageInactive: "text-gray-300 ring-1 ring-inset ring-gray-600 hover:bg-gray-700",
        pageActive: "z-10 bg-blue-600 text-white",
        ellipsis: "text-gray-500",
        selectBg: "bg-gray-800 border-gray-600 text-gray-300",
      }
    : {
        text: "text-gray-700",
        textMuted: "text-gray-500",
        textDisabled: "text-gray-300",
        btnBg: "bg-white border-gray-300",
        btnHover: "hover:bg-gray-50",
        btnText: "text-gray-700",
        pageInactive: "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
        pageActive: "z-10 bg-blue-600 text-white",
        ellipsis: "text-gray-700",
        selectBg: "border-gray-300 text-gray-700",
      };

  return (
    <div className="flex flex-col space-y-4 py-4 sm:flex-row sm:justify-between sm:items-center">
      {/* Mobile view */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium ${t.btnBg} ${
            currentPage === 1 ? t.textDisabled : `${t.btnText} ${t.btnHover}`
          }`}
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`relative ml-3 inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium ${t.btnBg} ${
            currentPage === totalPages
              ? t.textDisabled
              : `${t.btnText} ${t.btnHover}`
          }`}
        >
          Next
        </button>
      </div>

      {/* Desktop view - 3 column layout */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        {/* Left: Results count */}
        <div>
          <p className={`text-sm ${t.text}`}>
            Showing{" "}
            <span className="font-medium">
              {totalItems === 0 ? 0 : (currentPage - 1) * limit + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * limit, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> results
          </p>
        </div>

        {/* Center: Page navigation */}
        <div className="flex justify-center flex-grow mx-4">
          <nav
            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
            aria-label="Pagination"
          >
            <button
          type="button"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                currentPage === 1 ? t.textDisabled : `${t.textMuted} ${t.btnHover}`
              }`}
            >
              <span className="sr-only">Previous</span>
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
          type="button"
                    key={page}
                    onClick={() => onPageChange(page)}
                    aria-current={page === currentPage ? "page" : undefined}
                    aria-label={`Page ${page}`}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      page === currentPage
                        ? t.pageActive
                        : `${t.pageInactive} focus:z-20 focus:outline-offset-0`
                    }`}
                  >
                    {page}
                  </button>
                );
              }
              if (
                (page === 2 && currentPage > 3) ||
                (page === totalPages - 1 && currentPage < totalPages - 2)
              ) {
                return (
                  <span
                    key={page}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${t.ellipsis}`}
                  >
                    ...
                  </span>
                );
              }
              return null;
            })}
            <button
          type="button"
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                currentPage === totalPages
                  ? t.textDisabled
                  : `${t.textMuted} ${t.btnHover}`
              }`}
            >
              <span className="sr-only">Next</span>
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </nav>
        </div>

        {/* Right: Items per page selector */}
        <div className="flex items-center space-x-2">
          <span className={`text-sm ${t.text}`}>Show</span>
          <select
            aria-label="Rows per page"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className={`rounded-md border py-1 pl-3 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 ${t.selectBg}`}
          >
            {limitOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default Pagination;
